import React, { useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Anchor,
  Button,
  FileButton,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { UploadIcon } from '@radix-ui/react-icons'

import {
  DESIGNATION_OPTIONS,
  EXHIBIT_FILE_ACCEPT,
  INCLUSION_OPTIONS,
  isAcceptedExhibitFile,
  isDesignated,
  notifyUnsupported,
} from './exhibits'

import {
  CourtReporterExhibit,
  ExhibitInclusion,
  ExhibitRecordDesignation,
} from '@/apiClient'
import {
  ExhibitSubmissionEvent,
  useEditExhibitMutation,
} from '@/queries/courtReporterExhibits'
import { getApiErrorMessage } from '@/utils/apiError'

export const EditExhibitModal: React.FC<{
  event: ExhibitSubmissionEvent
  exhibit: CourtReporterExhibit | null
  onClose: () => void
}> = ({ event, exhibit, onClose }) => {
  const { mutate: edit, isLoading: saving } = useEditExhibitMutation(event)

  return (
    <Modal
      opened={exhibit !== null}
      onClose={onClose}
      title={exhibit ? `Edit exhibit ${exhibit.name}` : undefined}
      size="lg"
      centered
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
      withCloseButton={!saving}
    >
      {exhibit && (
        <EditExhibitForm
          key={exhibit.id}
          exhibit={exhibit}
          edit={edit}
          saving={saving}
          onClose={onClose}
        />
      )}
    </Modal>
  )
}

const EditExhibitForm: React.FC<{
  exhibit: CourtReporterExhibit
  edit: ReturnType<typeof useEditExhibitMutation>['mutate']
  saving: boolean
  onClose: () => void
}> = ({ exhibit, edit, saving, onClose }) => {
  const [name, setName] = useState(exhibit.name)
  const [description, setDescription] = useState(exhibit.description)
  const [designation, setDesignation] = useState<ExhibitRecordDesignation>(
    exhibit.designation,
  )
  const [inclusion, setInclusion] = useState<ExhibitInclusion>(
    exhibit.inclusion,
  )
  const [notes, setNotes] = useState(exhibit.notes ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [removeFile, setRemoveFile] = useState(false)
  const [validated, setValidated] = useState(false)
  const resetFileInput = useRef<() => void>(null)

  const currentFile = removeFile ? null : exhibit.file

  const chooseFile = (chosen: File | null) => {
    if (chosen && !isAcceptedExhibitFile(chosen)) {
      notifyUnsupported(chosen)
      return
    }
    setFile(chosen)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    disabled: saving,
    onDrop: (accepted, rejected) => {
      if (rejected.length > 0) {
        showNotification({
          color: 'red',
          message: 'An exhibit takes one file. Drop a single file.',
        })
        return
      }
      chooseFile(accepted[0] ?? null)
    },
  })

  const save = () => {
    setValidated(true)
    if (!name.trim() || !isDesignated(designation)) {
      return
    }

    edit(
      {
        exhibit,
        changes: {
          name: name.trim(),
          description,
          designation,
          inclusion,
          notes,
          ...(removeFile && !file ? { remove_file: true } : {}),
        },
        file: file ?? undefined,
      },
      {
        onSuccess: () => {
          showNotification({ color: 'green', message: 'Exhibit saved.' })
          onClose()
        },
        onError: error =>
          showNotification({
            color: 'red',
            message: getApiErrorMessage(error, 'Could not save the exhibit'),
          }),
      },
    )
  }

  return (
    <div {...getRootProps()} className="relative">
      <input {...getInputProps()} accept={EXHIBIT_FILE_ACCEPT} />
      {isDragActive && (
        <div className="pointer-events-none absolute -inset-2 z-10 flex flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-indigo-500 bg-white/95">
          <UploadIcon width={32} height={32} />
          <Text weight={600}>
            {currentFile || file
              ? 'Drop a file to replace the current one'
              : 'Drop a file to attach it'}
          </Text>
          <Text size="sm" color="dimmed">
            It uploads when you save.
          </Text>
        </div>
      )}
      <Stack>
        <TextInput
          label="Exhibit number"
          required
          value={name}
          disabled={saving}
          error={
            validated && !name.trim() ? 'Exhibit number is required.' : null
          }
          onChange={e => setName(e.currentTarget.value)}
        />
        <Textarea
          label="Description"
          autosize
          minRows={2}
          maxRows={8}
          value={description}
          disabled={saving}
          onChange={e => setDescription(e.currentTarget.value)}
        />
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'xs', cols: 1 }]}>
          <Select
            label="Designation"
            required
            placeholder="Select"
            data={DESIGNATION_OPTIONS}
            value={isDesignated(designation) ? designation : null}
            disabled={saving}
            error={
              validated && !isDesignated(designation)
                ? 'Designation is required.'
                : null
            }
            onChange={value =>
              setDesignation(
                (value as ExhibitRecordDesignation | null) ??
                  ExhibitRecordDesignation.unknown,
              )
            }
            withinPortal
          />
          <Select
            label="Inclusion"
            placeholder="Select"
            data={INCLUSION_OPTIONS}
            value={inclusion === ExhibitInclusion.unknown ? null : inclusion}
            disabled={saving}
            clearable
            onChange={value =>
              setInclusion(
                (value as ExhibitInclusion | null) ?? ExhibitInclusion.unknown,
              )
            }
            withinPortal
          />
        </SimpleGrid>
        <Textarea
          label="Notes"
          placeholder="Anything Filevine should know"
          autosize
          minRows={2}
          maxRows={8}
          value={notes}
          disabled={saving}
          onChange={e => setNotes(e.currentTarget.value)}
        />

        <Stack spacing={4}>
          <Text size="sm" weight={500}>
            File
          </Text>
          <Group spacing="xs">
            {file ? (
              <Text size="sm">{file.name}</Text>
            ) : currentFile ? (
              currentFile.url ? (
                <Anchor size="sm" href={currentFile.url} target="_blank">
                  {currentFile.filename}
                </Anchor>
              ) : (
                <Text size="sm">{currentFile.filename} (processing)</Text>
              )
            ) : (
              <Text size="sm" color="dimmed">
                No file attached
              </Text>
            )}
          </Group>
          <Group spacing="xs">
            <FileButton
              accept={EXHIBIT_FILE_ACCEPT}
              resetRef={resetFileInput}
              onChange={chosen => {
                resetFileInput.current?.()
                chooseFile(chosen)
              }}
            >
              {props => (
                <Button
                  {...props}
                  size="xs"
                  variant="default"
                  leftIcon={<UploadIcon />}
                  disabled={saving}
                >
                  {currentFile || file ? 'Replace file' : 'Upload file'}
                </Button>
              )}
            </FileButton>
            {(currentFile || file) && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                disabled={saving}
                onClick={() => {
                  setFile(null)
                  setRemoveFile(
                    exhibit.file !== null && exhibit.file !== undefined,
                  )
                }}
              >
                Remove file
              </Button>
            )}
          </Group>
        </Stack>

        <Group position="right" spacing="xs">
          <Button
            variant="light"
            color="gray"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button loading={saving} onClick={save}>
            Save changes
          </Button>
        </Group>
      </Stack>
    </div>
  )
}
